// frontend/src/components/DriverDashboard.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, User, Briefcase, AlertCircle, BookOpen, Car, ListOrdered } from 'lucide-react';
import axios from '../axiosConfig';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatDate, formatTimeString } from '../utils/dateTimeFormatter';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedCard, ThemedButton, ThemedBadge } from './themed';
import { usePreferences } from '../contexts/PreferenceContext';
import translationService from '../services/translationService';
import AppImage from './common/AppImage';
import TripWaypointReorderModal from './dialogs/TripWaypointReorderModal';
import { canLaunchWaypointReorder } from '../utils/waypointReorderAccess';

const DriverDashboard = ({
    currentEntity,
    activeTab = 'findTrip',
    panelIds = {
        myTrips: 'driver-dashboard-panel-myTrips',
        findTrip: 'driver-dashboard-panel-findTrip'
    },
    tabPrefix = 'driver-dashboard'
}) => {
    const { t, i18n } = useTranslation();
    const { background, text, border, cn } = useTheme();
    const navigate = useNavigate();
    const [availableTrips, setAvailableTrips] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [myTrips, setMyTrips] = useState([]);
    const [selectedTripId, setSelectedTripId] = useState(null);
    const [selectedVehicleId, setSelectedVehicleId] = useState(null);
    const [assignmentConflict, setAssignmentConflict] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showReorderModal, setShowReorderModal] = useState(false);
    const [reorderTrip, setReorderTrip] = useState(null);
    const { entitySettings } = usePreferences();
    const currentEntityIdRef = useRef(currentEntity?.entityId);
    const dashboardRefreshGenerationRef = useRef(0);
    currentEntityIdRef.current = currentEntity?.entityId;

    const beginTripPublication = useCallback((entityId) => {
        const normalizedEntityId = String(entityId);
        // A request started under the previous entity may resolve after the
        // user switches context. It must not consume a generation owned by the
        // new entity, or it can suppress that entity's legitimate in-flight
        // dashboard load even though the stale result cannot publish.
        if (String(currentEntityIdRef.current) !== normalizedEntityId) {
            return null;
        }
        return {
            entityId: normalizedEntityId,
            generation: ++dashboardRefreshGenerationRef.current
        };
    }, []);

    const ownsTripPublication = useCallback((owner) => (
        Boolean(owner)
        && owner.generation === dashboardRefreshGenerationRef.current
        && String(currentEntityIdRef.current) === owner.entityId
    ), []);

    const publishDashboardTrips = useCallback((allTrips, owner) => {
        if (!Array.isArray(allTrips) || !ownsTripPublication(owner)) return false;
        const currentUserId = Number(localStorage.getItem('userId'));
        setMyTrips(allTrips.filter((trip) => (
            ['scheduled', 'dispatched', 'in_progress'].includes(trip.status)
            && Number(trip.driver_id) === currentUserId
        )));
        setAvailableTrips(allTrips.filter((trip) => (
            ['scheduled', 'dispatched'].includes(trip.status)
            && trip.driver_id == null
        )));
        return true;
    }, [ownsTripPublication]);

    console.log('currentEntity:', currentEntity);

    const fetchAllTrips = useCallback(async (entityId, { shouldReportError } = {}) => {
        try {
            const response = await axios.get(`/api/trips/${entityId}/driverTrips`);
            console.log('All trips response:', response);
            let fetchedTrips = response.data?.data || [];
            const defaultLanguage = entitySettings?.entity_default_language || currentEntity?.entity_default_language || 'en';
            const shouldTranslateContent = entitySettings?.multi_language_enabled === true && i18n.language !== defaultLanguage;

            // Apply translations if multi-language is enabled and user language is not default
            if (shouldTranslateContent && fetchedTrips.length > 0) {
                console.log('Multi-language enabled, fetching translations for driver trips');

                try {
                    // Get unique route IDs for translation
                    const uniqueRouteIds = [...new Set(fetchedTrips.map(trip => trip.route_id))];
                    const routeItems = uniqueRouteIds.map(routeId => ({
                        id: routeId,
                        fields: {
                            name: fetchedTrips.find(t => t.route_id === routeId)?.route_name || ''
                        }
                    }));

                    // Batch fetch route translations
                    const routeTranslations = await translationService.batchGetTranslations(
                        entityId,
                        'route',
                        routeItems,
                        i18n.language
                    );

                    // Parse pickup and dropoff waypoints with IDs for translation
                    const waypointIds = new Set();
                    const waypointData = new Map(); // waypointId -> { name, type }

                    fetchedTrips.forEach(trip => {
                        // Parse pickup stops with IDs - format: "1:Dynacare,2:Hotel"
                        if (trip.pickup_stops_with_ids) {
                            const pickupStops = trip.pickup_stops_with_ids.split(',');
                            pickupStops.forEach(stop => {
                                const [id, name] = stop.split(':');
                                if (id && name) {
                                    const waypointId = parseInt(id.trim());
                                    const waypointName = name.trim();
                                    waypointIds.add(waypointId);
                                    waypointData.set(waypointId, {
                                        name: waypointName,
                                        type: 'pickup'
                                    });
                                }
                            });
                        }

                        // Parse dropoff stops with IDs - same format
                        if (trip.dropoff_stops_with_ids) {
                            const dropoffStops = trip.dropoff_stops_with_ids.split(',');
                            dropoffStops.forEach(stop => {
                                const [id, name] = stop.split(':');
                                if (id && name) {
                                    const waypointId = parseInt(id.trim());
                                    const waypointName = name.trim();
                                    waypointIds.add(waypointId);
                                    waypointData.set(waypointId, {
                                        name: waypointName,
                                        type: 'dropoff'
                                    });
                                }
                            });
                        }
                    });

                    // Batch fetch waypoint translations if we have waypoints
                    let waypointTranslations = new Map();
                    if (waypointIds.size > 0) {
                        try {
                            const waypointItems = Array.from(waypointIds).map(waypointId => ({
                                id: waypointId,
                                fields: {
                                    name: waypointData.get(waypointId)?.name || ''
                                }
                            }));

                            waypointTranslations = await translationService.batchGetTranslations(
                                entityId,
                                'waypoint',
                                waypointItems,
                                i18n.language
                            );

                            console.log('Waypoint translations fetched:', waypointTranslations);
                        } catch (error) {
                            console.error('Error fetching waypoint translations:', error);
                        }
                    }

                    // Apply route name and waypoint translations
                    fetchedTrips = fetchedTrips.map(trip => {
                        let updatedTrip = { ...trip };

                        // Apply route name translation
                        const routeTranslation = routeTranslations.get(trip.route_id);
                        if (routeTranslation && routeTranslation.name) {
                            updatedTrip.route_name = routeTranslation.name.content || trip.route_name;
                        }

                        // Apply waypoint translations to pickup stops
                        if (trip.pickup_stops_with_ids && waypointTranslations.size > 0) {
                            const pickupStops = trip.pickup_stops_with_ids.split(',');
                            const translatedPickupStops = pickupStops.map(stop => {
                                const [id, originalName] = stop.split(':');
                                if (id && originalName) {
                                    const waypointId = parseInt(id.trim());
                                    const translation = waypointTranslations.get(waypointId);
                                    const translatedName = translation?.name?.content || originalName.trim();
                                    return translatedName;
                                }
                                return originalName;
                            });
                            updatedTrip.pickup_stops = translatedPickupStops.join(', ');
                        }

                        // Apply waypoint translations to dropoff stops
                        if (trip.dropoff_stops_with_ids && waypointTranslations.size > 0) {
                            const dropoffStops = trip.dropoff_stops_with_ids.split(',');
                            const translatedDropoffStops = dropoffStops.map(stop => {
                                const [id, originalName] = stop.split(':');
                                if (id && originalName) {
                                    const waypointId = parseInt(id.trim());
                                    const translation = waypointTranslations.get(waypointId);
                                    const translatedName = translation?.name?.content || originalName.trim();
                                    return translatedName;
                                }
                                return originalName;
                            });
                            updatedTrip.dropoff_stops = translatedDropoffStops.join(', ');
                        }

                        return updatedTrip;
                    });

                    console.log('Driver trips translations applied successfully');
                } catch (translationError) {
                    console.error('Error applying translations to driver trips:', translationError);
                    // Continue with original data if translation fails
                }
            }

            return fetchedTrips;
        } catch (error) {
            if (typeof shouldReportError !== 'function' || shouldReportError()) {
                toast.error(t('Error fetching trips:'), error);
            }
            // Preserve the currently rendered owner state on a failed refresh;
            // an empty array is reserved for a successful response with no trips.
            return null;
        }
    }, [
        t,
        currentEntity?.entity_default_language,
        entitySettings?.entity_default_language,
        entitySettings?.multi_language_enabled,
        i18n.language
    ]);
    
    const fetchAvailableVehicles = useCallback(async (entityId) => {
        try {
            const response = await axios.get(`/api/vehicles/${entityId}/available`);
            console.log('Available Vehicles response:', response);
            return response.data?.data || [];
        } catch (error) {
            toast.error(t('Error fetching available vehicles:'), error);
            return [];
        }
    }, [t]);

    useEffect(() => {
        dashboardRefreshGenerationRef.current += 1;
        setAvailableTrips([]);
        setVehicles([]);
        setMyTrips([]);
        setSelectedTripId(null);
        setSelectedVehicleId(null);
        setShowReorderModal(false);
        setReorderTrip(null);
    }, [currentEntity?.entityId]);

    useEffect(() => {
        const entityId = currentEntity?.entityId;
        if (!entityId) return;

        setIsLoading(true);

        const fetchAllData = async () => {
            try {
                const owner = beginTripPublication(entityId);
                if (!owner) return;
                const [allTrips, vehicleList] = await Promise.all([
                    fetchAllTrips(entityId, {
                        shouldReportError: () => ownsTripPublication(owner)
                    }),
                    fetchAvailableVehicles(entityId)
                ]);

                if (String(currentEntityIdRef.current) !== String(entityId)) {
                    return;
                }
                setVehicles(vehicleList);
                publishDashboardTrips(allTrips, owner);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                if (String(currentEntityIdRef.current) === String(entityId)) {
                    setIsLoading(false);
                }
            }
        };

        fetchAllData();
    }, [
        currentEntity?.entityId,
        beginTripPublication,
        fetchAllTrips,
        fetchAvailableVehicles,
        ownsTripPublication,
        publishDashboardTrips
    ]);

    // Handle language changes - clear cache and refresh data
    useEffect(() => {
        const handleLanguageChange = (lng) => {
            console.log('Language changed to:', lng);
            console.log('EntitySettings during language change:', entitySettings);
            if (currentEntity?.entityId && entitySettings) {
                console.log('Re-fetching driver trips for new language:', lng);
                console.log('Multi-language enabled:', entitySettings.multi_language_enabled);
                // Clear the translation cache first
                translationService.clearCache();
                // Re-fetch trips with new translations
                const entityId = currentEntity.entityId;
                setIsLoading(true);

                const fetchAllData = async () => {
                    try {
                        const owner = beginTripPublication(entityId);
                        if (!owner) return;
                        const [allTrips, vehicleList] = await Promise.all([
                            fetchAllTrips(entityId, {
                                shouldReportError: () => ownsTripPublication(owner)
                            }),
                            fetchAvailableVehicles(entityId)
                        ]);

                        if (String(currentEntityIdRef.current) !== String(entityId)) {
                            return;
                        }
                        setVehicles(vehicleList);
                        publishDashboardTrips(allTrips, owner);
                    } catch (error) {
                        console.error('Error fetching dashboard data:', error);
                    } finally {
                        if (String(currentEntityIdRef.current) === String(entityId)) {
                            setIsLoading(false);
                        }
                    }
                };

                fetchAllData();
            }
        };

        i18n.on('languageChanged', handleLanguageChange);

        // Cleanup
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [
        currentEntity?.entityId,
        entitySettings,
        beginTripPublication,
        fetchAllTrips,
        fetchAvailableVehicles,
        i18n,
        ownsTripPublication,
        publishDashboardTrips
    ]);


    // function to handle assigning a trip  
    const handleAssignTrip = useCallback(async (overrideVehicleConflict = false) => {
        if (!selectedTripId || !selectedVehicleId || !currentEntity?.entityId) {
            alert('Please select both a trip and a vehicle');
            return;
        }

        const selectedTrip = availableTrips.find(trip => String(trip.id) === String(selectedTripId));
        const selectedVehicle = vehicles.find(vehicle => String(vehicle.id) === String(selectedVehicleId));
        if (!selectedTrip || !selectedVehicle) {
            setSelectedTripId(null);
            setSelectedVehicleId(null);
            toast.error(t('Invalid trip or vehicle selection'));
            return;
        }

        try {
            const payload = { vehicle_id: selectedVehicleId };
            if (overrideVehicleConflict) {
                payload.override_vehicle_conflict = true;
            }
            const response = await axios.post(`/api/trips/${currentEntity.entityId}/${selectedTripId}/assign`, payload);

            if (response.status === 200) {
                const entityId = currentEntity.entityId;
                const owner = beginTripPublication(entityId);
                if (!owner) return;
                const allTrips = await fetchAllTrips(entityId, {
                    shouldReportError: () => ownsTripPublication(owner)
                });
                publishDashboardTrips(allTrips, owner);
                setSelectedTripId(null);
                setSelectedVehicleId(null);
                setAssignmentConflict(null);
                toast.success(t('Trip assigned successfully'));
            }
        } catch (error) {
            const errorCode = error?.response?.data?.code;
            const backendError = error?.response?.data?.error;
            if (errorCode === 'NO_ACTIVE_SESSION') {
                toast.error(t('driverDashboardAssignmentBlockedNoActiveSession'));
            } else if (errorCode === 'VEHICLE_ACTIVE_TRIP_CONFLICT') {
                setAssignmentConflict({
                    blockingTrips: error.response?.data?.blockingTrips || []
                });
            } else {
                toast.error(backendError || t('driverDashboardAssignTripFailed'));
            }
        }
    }, [
        selectedTripId,
        selectedVehicleId,
        currentEntity?.entityId,
        availableTrips,
        vehicles,
        beginTripPublication,
        fetchAllTrips,
        ownsTripPublication,
        publishDashboardTrips,
        t
    ]);

    const handleDispatchTrip = useCallback(async (tripId) => {
        if (!currentEntity?.entityId || !tripId) {
            return;
        }

        if (!myTrips.some(trip => String(trip.id) === String(tripId))) {
            return;
        }

        try {
            const response = await axios.post(`/api/trips/${currentEntity.entityId}/${tripId}/dispatch`);

            if (response.status === 200) {
                const entityId = currentEntity.entityId;
                const owner = beginTripPublication(entityId);
                if (!owner) return;
                const allTrips = await fetchAllTrips(entityId, {
                    shouldReportError: () => ownsTripPublication(owner)
                });
                publishDashboardTrips(allTrips, owner);
                toast.success(t('tripDispatchedSuccessfully'));
            }
        } catch (error) {
            console.error('Error dispatching trip:', error);
            toast.error(t('errorDispatchingTrip'));
        }
    }, [
        currentEntity?.entityId,
        myTrips,
        beginTripPublication,
        fetchAllTrips,
        ownsTripPublication,
        publishDashboardTrips,
        t
    ]);

    // function to handle starting a trip - navigate to trips page with tripId in state
    const handleStartTrip = useCallback((tripId) => {
        if (!myTrips.some(trip => String(trip.id) === String(tripId))) {
            return;
        }
        navigate('/trips', { state: { tripId } });
    }, [myTrips, navigate]);

    const handleOpenReorderModal = useCallback((trip) => {
        if (!myTrips.some(t => String(t.id) === String(trip?.id))) {
            return;
        }
        setReorderTrip(trip);
        setShowReorderModal(true);
    }, [myTrips]);

    const refreshDashboardTrips = useCallback(async () => {
        const entityId = currentEntity?.entityId;
        if (!entityId) return;
        const owner = beginTripPublication(entityId);
        if (!owner) return;
        const allTrips = await fetchAllTrips(entityId, {
            shouldReportError: () => ownsTripPublication(owner)
        });
        publishDashboardTrips(allTrips, owner);
    }, [
        currentEntity?.entityId,
        beginTripPublication,
        fetchAllTrips,
        ownsTripPublication,
        publishDashboardTrips
    ]);

    // Capacity validation logic
    const getCapacityValidation = useCallback(() => {
        if (!selectedTripId || !selectedVehicleId) {
            return { isValid: true, message: null, hasBiggerVehicle: false };
        }

        const selectedTrip = availableTrips.find(t => t.id === selectedTripId);
        const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId);

        if (!selectedTrip || !selectedVehicle) {
            return { isValid: true, message: null, hasBiggerVehicle: false };
        }

        const tripRiders = selectedTrip.total_riders || 0;
        const tripLuggage = selectedTrip.total_luggages || 0;
        const vehicleCapacity = selectedVehicle.capacity || 0;
        const vehicleLuggageCapacity = selectedVehicle.luggage_capacity || 0;

        // Check if capacity is exceeded (0 means unlimited, so skip check)
        const isPassengerOverCapacity = vehicleCapacity > 0 && tripRiders > vehicleCapacity;
        const isLuggageOverCapacity = vehicleLuggageCapacity > 0 && tripLuggage > vehicleLuggageCapacity;

        if (!isPassengerOverCapacity && !isLuggageOverCapacity) {
            return { isValid: true, message: null, hasBiggerVehicle: false };
        }

        // Check if there's a bigger vehicle available
        const hasBiggerVehicle = vehicles.some(v => {
            if (v.id === selectedVehicleId) return false;
            const canFitPassengers = v.capacity === 0 || v.capacity >= tripRiders;
            const canFitLuggage = v.luggage_capacity === 0 || v.luggage_capacity >= tripLuggage;
            return canFitPassengers && canFitLuggage;
        });

        // Build message
        let message = '';
        if (isPassengerOverCapacity && isLuggageOverCapacity) {
            message = t('overCapacityBoth', {
                riders: tripRiders,
                passengerCapacity: vehicleCapacity,
                luggage: tripLuggage,
                luggageCapacity: vehicleLuggageCapacity
            });
        } else if (isPassengerOverCapacity) {
            message = t('overCapacityPassengers', {
                riders: tripRiders,
                capacity: vehicleCapacity
            });
        } else {
            message = t('overCapacityLuggage', {
                luggage: tripLuggage,
                capacity: vehicleLuggageCapacity
            });
        }

        return {
            isValid: false,
            message,
            hasBiggerVehicle
        };
    }, [selectedTripId, selectedVehicleId, availableTrips, vehicles, t]);

    const capacityValidation = getCapacityValidation();

    const renderPanelMessage = (message) => (
        <div className={cn("py-6", background.primary)}>
            <section
                id={panelIds.myTrips}
                role="tabpanel"
                aria-labelledby={`${tabPrefix}-tab-myTrips`}
                hidden={activeTab !== 'myTrips'}
                className="py-8"
            >
                <div className={cn("text-center", text.muted)}>{message}</div>
            </section>
            <section
                id={panelIds.findTrip}
                role="tabpanel"
                aria-labelledby={`${tabPrefix}-tab-findTrip`}
                hidden={activeTab !== 'findTrip'}
                className="py-8"
            >
                <div className={cn("text-center", text.muted)}>{message}</div>
            </section>
        </div>
    );

    if (!currentEntity?.entityId) {
        return renderPanelMessage(t('loading'));
    }

    if (isLoading) {
        return renderPanelMessage(t('loading'));
    }

    return (
        <div className={cn("py-6", background.primary)}>
            <section
                id={panelIds.findTrip}
                role="tabpanel"
                aria-labelledby={`${tabPrefix}-tab-findTrip`}
                hidden={activeTab !== 'findTrip'}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Available Trips */}
                <ThemedCard>
                    <ThemedCard.Header>
                        <div className="flex items-center gap-3">
                            <span>{t('availableTrips')}</span>
                            <ThemedBadge variant="primary">
                                {availableTrips.length}
                            </ThemedBadge>
                        </div>
                    </ThemedCard.Header>
                    <ThemedCard.Body>
                        <div className="space-y-4">
                            {availableTrips.map(trip => (
                                <ThemedCard 
                                    key={trip.id}
                                    className="cursor-pointer"
                                    padding="sm"
                                    hoverable={true}
                                    selected={selectedTripId === trip.id}
                                    onClick={() => setSelectedTripId(trip.id)}
                                >
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <MapPin className={cn("h-4 w-4", text.muted)} />
                                            <span className={text.secondary}>
                                                <span className={text.muted}>{t('route')}:</span> {trip.route_name}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Calendar className={cn("h-4 w-4", text.muted)} />
                                            <span className={text.secondary}>
                                                <span className={text.muted}>{t('date')}:</span> {formatDate(trip.trip_date)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Clock className={cn("h-4 w-4", text.muted)} />
                                            <span className={text.secondary}>
                                                <span className={text.muted}>{t('departureTime')}:</span> {formatTimeString(trip.departure_time)} ({t('startLocation')})
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Car className={cn("h-4 w-4", text.muted)} />
                                            <span className={text.secondary}>
                                                <span className={text.muted}>{t('Vehicle')}:</span> {trip.vehicle_number || t("notAssigned")}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <User className={cn("h-4 w-4", text.muted)} />
                                            <span className={text.secondary}>
                                                <span className={text.muted}>{t('driver')}:</span> {trip.driver_name || t("notAssigned")}
                                            </span>
                                        </div>
                                        <div className="space-y-2 mt-3">
                                            <div className="flex items-center gap-2">
                                                <BookOpen className={cn("h-4 w-4", text.muted)} />
                                                <span className={text.secondary}>
                                                    <span className={text.muted}>{t('totalBookings')}:</span> {trip.total_bookings}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Users className={cn("h-4 w-4", text.muted)} />
                                                <span className={text.secondary}>
                                                    <span className={text.muted}>{t('totalRiders')}:</span> {trip.total_riders}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Briefcase className={cn("h-4 w-4", text.muted)} />
                                                <span className={text.secondary}>
                                                    <span className={text.muted}>{t('totalLuggage')}:</span> {trip.total_luggages}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-start gap-2">
                                                <MapPin className={cn("h-4 w-4 mt-0.5", text.muted)} />
                                                <span className={cn("text-sm", text.secondary)}>
                                                    <span className={text.muted}>{t('pickupLocation')} ({trip.pickup_count}):</span><br />
                                                    {trip.pickup_stops}
                                                </span>
                                            </div>
                                            <div className="flex items-start gap-2">
                                                <MapPin className={cn("h-4 w-4 mt-0.5", text.muted)} />
                                                <span className={cn("text-sm", text.secondary)}>
                                                    <span className={text.muted}>{t('dropoffLocation')} ({trip.dropoff_count}):</span><br />
                                                    {trip.dropoff_stops}
                                                </span>
                                            </div>
                                        </div>
                                        <div className={cn("flex items-center justify-between pt-3 border-t", border.default)}>
                                            <div className="flex items-center gap-2">
                                                <AlertCircle className={cn("h-4 w-4", text.muted)} />
                                                <span className={text.muted}>{t('status')}:</span>
                                                <ThemedBadge 
                                                    variant={trip.status === 'scheduled' ? 'warning' : 'primary'}
                                                >
                                                    {t(`tripStatus.${trip.status}`)}
                                                </ThemedBadge>
                                            </div>
                                        </div>
                                    </div>
                                </ThemedCard>
                            ))}
                        </div>
                    </ThemedCard.Body>
                </ThemedCard>

                {/* Available vehicles */}
                <ThemedCard>
                    <ThemedCard.Header>
                        <div className="flex items-center gap-3">
                            <span>{t('availableVehicles')}</span>
                            <ThemedBadge variant="primary">
                                {vehicles.length}
                            </ThemedBadge>
                        </div>
                    </ThemedCard.Header>
                    <ThemedCard.Body>
                        <div className="space-y-4">
                            {vehicles.map(vehicle => (
                                <ThemedCard 
                                    key={vehicle.id}
                                    className="cursor-pointer"
                                    padding="sm"
                                    hoverable={true}
                                    selected={selectedVehicleId === vehicle.id}
                                    onClick={() => setSelectedVehicleId(vehicle.id)}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="flex-1 space-y-3">
                                            <div className="flex items-center gap-2">
                                                <Car className={cn("h-4 w-4", text.muted)} />
                                                <span className={text.primary}>{vehicle.vehicle_number}</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="flex items-center gap-2">
                                                    <Users className={cn("h-4 w-4", text.muted)} />
                                                    <span className={text.secondary}>
                                                        <span className={text.muted}>{t('capacity')}:</span> {vehicle.capacity} {t('passengers')}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Briefcase className={cn("h-4 w-4", text.muted)} />
                                                    <span className={text.secondary}>
                                                        <span className={text.muted}>{t('luggageCapacity')}:</span> {vehicle.luggage_capacity} {t('luggage')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        {vehicle.picture_url && (
                                            <div className="flex-shrink-0">
                                                <AppImage
                                                    src={vehicle.picture_url}
                                                    alt={vehicle.vehicle_number}
                                                    crossOrigin="anonymous"
                                                    fallbackSrc={null}
                                                    className={cn(
                                                        "w-20 h-20 rounded-lg object-contain bg-gray-50",
                                                        border.default
                                                    )}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </ThemedCard>
                            ))}
                        </div>
                    </ThemedCard.Body>
                </ThemedCard>
                </div>

                {/* Assign Trip Button */}
                <div className="mt-6 flex flex-col items-center gap-2">
                {!capacityValidation.isValid && (
                    <div className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg",
                        "bg-red-500/10 border border-red-500/30"
                    )}>
                        <AlertCircle className="h-5 w-5 text-red-500" />
                        <div className="text-red-500 text-sm">
                            <span>{capacityValidation.message}</span>
                            {capacityValidation.hasBiggerVehicle && (
                                <span className="ml-1 font-medium">
                                    {t('selectBiggerShuttle')}
                                </span>
                            )}
                        </div>
                    </div>
                )}
                {assignmentConflict && (
                    <div className={cn(
                        "flex flex-col gap-2 px-4 py-3 rounded-lg max-w-xl",
                        "bg-amber-500/10 border border-amber-500/30"
                    )}>
                        <div className="flex items-start gap-2 text-amber-600 dark:text-amber-300">
                            <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                                <div className="font-medium">{t('vehicleConflict.title')}</div>
                                <div>{t('vehicleConflict.message')}</div>
                                {assignmentConflict.blockingTrips?.map((trip) => (
                                    <div key={trip.tripId}>
                                        {t('vehicleConflict.blockingTrip', {
                                            tripId: trip.tripId,
                                            vehicleName: trip.vehicleName || trip.vehicleId,
                                            routeName: trip.routeName || ''
                                        })}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <ThemedButton
                            onClick={() => handleAssignTrip(true)}
                            variant="primary"
                            size="sm"
                        >
                            {t('vehicleConflict.confirmOverride')}
                        </ThemedButton>
                    </div>
                )}
                <ThemedButton
                    onClick={() => handleAssignTrip(false)}
                    disabled={!selectedTripId || !selectedVehicleId || !capacityValidation.isValid}
                    variant="primary"
                    size="lg"
                >
                    {t('assignTrip')}
                </ThemedButton>
                </div>
            </section>

            {/* My Trips */}
            <section
                id={panelIds.myTrips}
                role="tabpanel"
                aria-labelledby={`${tabPrefix}-tab-myTrips`}
                hidden={activeTab !== 'myTrips'}
            >
                <ThemedCard>
                    <ThemedCard.Header>
                        <div className="flex items-center gap-3">
                            <span>{t('myTrips')}</span>
                            <ThemedBadge variant="success">
                                {myTrips.length}
                            </ThemedBadge>
                        </div>
                    </ThemedCard.Header>
                    <ThemedCard.Body>
                        <div className="space-y-4">
                            {myTrips.map(trip => (
                                <ThemedCard 
                                    key={trip.id} 
                                    padding="sm"
                                    className="border-l-4 border-l-blue-500"
                                >
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <MapPin className={cn("h-4 w-4", text.muted)} />
                                            <span className={text.secondary}>
                                                <span className={text.muted}>{t('route')}:</span> {trip.route_name}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Calendar className={cn("h-4 w-4", text.muted)} />
                                            <span className={text.secondary}>
                                                <span className={text.muted}>{t('date')}:</span> {formatDate(trip.trip_date)}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Clock className={cn("h-4 w-4", text.muted)} />
                                            <span className={text.secondary}>
                                                <span className={text.muted}>{t('departureTime')}:</span> {trip.departure_time} ({t('startLocation')})
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Car className={cn("h-4 w-4", text.muted)} />
                                            <span className={text.secondary}>
                                                <span className={text.muted}>{t('Vehicle')}:</span> {trip.vehicle_number}
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-1 gap-3 mt-3 sm:grid-cols-3 sm:gap-4">
                                            <div className="flex items-center gap-2">
                                                <BookOpen className={cn("h-4 w-4", text.muted)} />
                                                <span className={text.secondary}>
                                                    <span className={text.muted}>{t('totalBookings')}:</span> {trip.total_bookings}
                                                </span>
                                            </div>                            
                                            <div className="flex items-center gap-2">
                                                <Users className={cn("h-4 w-4", text.muted)} />
                                                <span className={text.secondary}>
                                                    <span className={text.muted}>{t('totalRiders')}:</span> {trip.total_riders}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Briefcase className={cn("h-4 w-4", text.muted)} />
                                                <span className={text.secondary}>
                                                    <span className={text.muted}>{t('totalLuggage')}:</span> {trip.total_luggages}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-start gap-2">
                                                <MapPin className={cn("h-4 w-4 mt-0.5", text.muted)} />
                                                <span className={cn("text-sm", text.secondary)}>
                                                    <span className={text.muted}>{t('pickupLocation')} ({trip.pickup_count}):</span><br />
                                                    {trip.pickup_stops?.split(';').map(stop => stop.split('|')[0]).join('; ')}
                                                </span>
                                            </div>
                                            <div className="flex items-start gap-2">
                                                <MapPin className={cn("h-4 w-4 mt-0.5", text.muted)} />
                                                <span className={cn("text-sm", text.secondary)}>
                                                    <span className={text.muted}>{t('dropoffLocation')} ({trip.dropoff_count}):</span><br />
                                                    {trip.dropoff_stops?.split(';').map(stop => stop.split('|')[0]).join('; ')}
                                                </span>
                                            </div>
                                        </div>
                                        <div className={cn("pt-3 border-t", border.default)}>
                                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                                <div className="flex items-center gap-2">
                                                    <AlertCircle className={cn("h-4 w-4", text.muted)} />
                                                    <span className={text.muted}>{t('status')}:</span>
                                                    <ThemedBadge 
                                                        variant={
                                                            trip.status === 'dispatched' ? 'warning' : 
                                                            trip.status === 'in_progress' ? 'primary' : 'default'
                                                        }
                                                    >
                                                        {t(`tripStatus.${trip.status}`)}
                                                    </ThemedBadge>
                                                </div>
                                                <div className="flex flex-wrap gap-2 sm:justify-end">
                                                    {canLaunchWaypointReorder({
                                                      trip,
                                                      status: trip.status,
                                                      role: currentEntity?.userRole,
                                                      userId: localStorage.getItem('userId')
                                                    }) && (
                                                        <ThemedButton
                                                            onClick={() => handleOpenReorderModal(trip)}
                                                            variant="primary"
                                                            size="sm"
                                                        >
                                                            <ListOrdered className="h-4 w-4 mr-1" />
                                                            {t('reorderStops')}
                                                        </ThemedButton>
                                                    )}
                                                    {trip.status === 'dispatched' && (
                                                        <ThemedButton
                                                            onClick={() => handleStartTrip(trip.id)}
                                                            variant="primary"
                                                            size="sm"
                                                        >
                                                            {t('startTrip')}
                                                        </ThemedButton>
                                                    )}
                                                    {trip.status === 'scheduled' && (
                                                        <ThemedButton
                                                            onClick={() => handleDispatchTrip(trip.id)}
                                                            variant="primary"
                                                            size="sm"
                                                        >
                                                            {t('dispatchTrip')}
                                                        </ThemedButton>
                                                    )}
                                                    {trip.status === 'in_progress' && (
                                                        <ThemedButton
                                                            onClick={() => handleStartTrip(trip.id)}
                                                            variant="primary"
                                                            size="sm"
                                                        >
                                                            {t('continueTrip')}
                                                        </ThemedButton>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </ThemedCard>
                            ))}
                        </div>
                    </ThemedCard.Body>
                </ThemedCard>
            </section>

            {/* Waypoint Reorder Modal */}
            <TripWaypointReorderModal
                isOpen={showReorderModal}
                onClose={() => {
                    setShowReorderModal(false);
                    setReorderTrip(null);
                }}
                entityId={currentEntity?.entityId}
                tripId={reorderTrip?.id}
                tripInfo={{
                    routeId: reorderTrip?.route_id,
                    routeName: reorderTrip?.route_name,
                    tripDate: reorderTrip?.trip_date,
                    departureTime: reorderTrip?.departure_time
                }}
                onOrderChanged={refreshDashboardTrips}
            />
        </div>
    );
};

export default React.memo(DriverDashboard);
